import React, { useState, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useCardList } from '@/hooks'
import PageLayout from '@/components/PageLayout'
import SkeletonBox from '@/components/SkeletonBox'
import NoCard from './components/no-card'
import ElectronicCard from './electronic-card'
import PhysicalCard from './physical-card'
import Ripple from '@/components/Ripple'
import { APP_NAME } from '@/config/constants'

// 卡片类型枚举
export const enum CardType {
  Virtual = 'virtual',
  Physical = 'physical'
}

// 本地存储 key
const CARD_TAB_KEY = 'card_v2_tab'

// 获取缓存的 tab（URL参数优先）
const getCachedTab = (urlTab?: string | null): CardType => {
  const tab = urlTab || localStorage.getItem(CARD_TAB_KEY)
  return tab === 'virtual' ? CardType.Virtual : CardType.Physical
}

// 头部组件 Props
interface CardHeaderProps {
  hasCard: boolean
  selectedCardType: CardType
  onCardTypeChange: (type: CardType) => void
  onAddCard: () => void
  onViewCardList: () => void
}

// 底部白色层，通过 Portal 挂载到 body，覆盖 Safari 工具栏
const BottomOverlay = () => createPortal(
  <div className="fixed left-0 right-0 bottom-0 h-50vh bg-white -z-24 pointer-events-none" />,
  document.body
)

// 骨架屏组件 - 模拟虚拟卡主页面布局
const CardSkeleton = () => (
  <div className="flex flex-col pb-10">
    {/* 卡片骨架 */}
    <div className="px-5 pt-2">
      <SkeletonBox className="w-full h-48 rounded-2xl bg-white" />
    </div>
    
    {/* 分页指示器骨架 */}
    <div className="flex justify-center gap-1.5 mt-4">
      <SkeletonBox className="w-2 h-2 rounded-full" />
      <SkeletonBox className="w-2 h-2 rounded-full" />
      <SkeletonBox className="w-2 h-2 rounded-full" />
    </div>

    {/* 操作按钮骨架 */}
    <div className="flex justify-between items-start px-5 mt-4">
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="flex flex-col items-center py-2">
          <SkeletonBox className="w-14 h-14 rounded-2xl" />
          <SkeletonBox className="w-8 h-3 rounded mt-2" />
        </div>
      ))}
    </div>

    {/* 交易记录骨架 */}
    <div className="px-5 mt-6">
      {/* 标题 */}
      <div className="flex items-center justify-between mb-3">
        <SkeletonBox className="w-20 h-5 rounded" />
      </div>
      
      {/* 记录列表 */}
      <div className="bg-white rounded-2xl overflow-hidden py-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex items-center py-4">
            <SkeletonBox className="w-10 h-10 rounded-full flex-shrink-0" />
            <div className="flex-1 ml-3">
              <SkeletonBox className="w-24 h-4 rounded mb-2" />
              <SkeletonBox className="w-32 h-3 rounded" />
            </div>
            <div className="text-right">
              <SkeletonBox className="w-16 h-4 rounded mb-2" />
              <SkeletonBox className="w-10 h-3 rounded ml-auto" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
)

// 头部组件
const CardHeader: React.FC<CardHeaderProps> = ({
  hasCard,
  selectedCardType,
  onCardTypeChange,
  onAddCard,
  onViewCardList
}) => {
  const { t } = useTranslation()

  // 有卡状态：显示 Tab 切换
  return (
    <div className="flex items-center justify-between px-5 pt-4 pb-4">
      {/* 左侧 Tab 切换 - 支持横向滚动，隐藏滚动条 */}
      <div className="flex-1 min-w-0 overflow-x-auto scrollbar-hide mr-3">
        <div className="flex items-center gap-4 whitespace-nowrap">
          {hasCard ? (  
            [
              { type: CardType.Physical, label: '实体卡' },
              { type: CardType.Virtual, label: '电子卡' },
            ].map(({ type, label }) => (
              <span
                key={type}
                className={`text-lg font-semibold cursor-pointer transition-colors ${
                  selectedCardType === type ? 'text-black' : 'text-[#939EC2]'
                }`}
                onClick={() => onCardTypeChange(type)}
              >
                {t(label)}
              </span>
            ))
          ) : (
            <span className="text-lg font-semibold text-black">{t('{{name}}卡片', { name: APP_NAME })}</span>
          )}
        </div>
      </div>

      {/* 右侧按钮组 */}
      <div className="flex items-center gap-3">
        {/* 查看卡组列表按钮 */}
        <Ripple className="rounded-full bg-white">
          <button
            onClick={onViewCardList}
            className="w-10 h-10 flex items-center justify-center rounded-full shadow-sm"
          >
            <img src="/images/card-v2/cards.svg" alt="list" className="w-5 h-5" />
          </button>
        </Ripple>
        {/* 添加卡片按钮 */}
        <Ripple className="rounded-full bg-white">
          <button
            onClick={onAddCard}
            className="w-10 h-10 flex items-center justify-center rounded-full shadow-sm"
          >
            <img src="/images/card-v2/add.svg" alt="add" className="w-4 h-4" />
          </button>
        </Ripple>
      </div>
    </div>
  )
}

export default React.memo(function CardV2() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  
  // 从 URL 参数或本地缓存获取初始 tab
  const [selectedCardType, setSelectedCardType] = useState<CardType>(() => 
    getCachedTab(searchParams.get('tab'))
  )

  // 使用 useCardList hook 获取卡片列表
  const { 
    virtualCards, 
    physicalCards, 
    hasVirtualCard, 
    hasPhysicalCard,
    hasCard,
    loading,
    actions
  } = useCardList()

  // 进入页面时刷新卡片列表 & 设置 body 背景色控制 iOS 状态栏
  useEffect(() => {
    actions.refreshCardList()
    
    const originalBg = document.body.style.backgroundColor
    document.body.style.backgroundColor = '#DBE0FF'
    return () => {
      document.body.style.backgroundColor = originalBg
    }
  }, [])

  // 切换 tab 时保存到本地缓存
  const handleCardTypeChange = useCallback((type: CardType) => {
    setSelectedCardType(type)
    localStorage.setItem(CARD_TAB_KEY, type)
  }, [])



  // 是否还在初始化（未获取过数据）
  const isInitializing = hasCard === null

  // 当前类型是否有卡
  const hasCurrentTypeCard = selectedCardType === CardType.Virtual ? hasVirtualCard : hasPhysicalCard

  // 处理添加卡片（传递当前选中的卡片类型）
  const handleAddCard = useCallback(() => {
    const typeParam = selectedCardType === CardType.Virtual ? 'electronic' : 'physical'
    navigate(`/card-v2/apply-card?type=${typeParam}`)
  }, [navigate, selectedCardType])

  // 查看卡组列表
  const handleViewCardList = useCallback(() => {
    navigate('/card-v2/card-list')
  }, [navigate])

  // 渲染内容区域
  const renderContent = () => {
    // 加载中或初始化时显示骨架屏
    if (loading || isInitializing) {
      return <CardSkeleton />
    }

    if (!hasCurrentTypeCard) {
      return (
        <NoCard
          type={selectedCardType === CardType.Virtual ? 'virtual' : 'physical'}
          title={t('您当前还没有可用卡片')}
          subtitle={t('可在下方快速添加')}
          buttonText={t('申请卡片')}
          onAdd={handleAddCard}
          className="flex-1"
        />
      )
    }

    // 有卡状态
    if (selectedCardType === CardType.Virtual) {
      return <ElectronicCard cards={virtualCards} />
    }

    // 实体卡
    return <PhysicalCard cards={physicalCards} />
  }

  return (
    <PageLayout tabbarPage={true} isHeader={false}>
      {/* 底部白色层 - Portal 到 body，覆盖 Safari 工具栏 */}
      <BottomOverlay />
      <div 
        className="min-h-full flex flex-col"
        style={{ background: 'linear-gradient(180deg, #DBE0FF 0%, #FFFFFF 43.78%)' }}
      >
        {/* 头部 */}
        <CardHeader
          hasCard={!isInitializing && (hasVirtualCard || hasPhysicalCard)}
          selectedCardType={selectedCardType}
          onCardTypeChange={handleCardTypeChange}
          onAddCard={handleAddCard}
          onViewCardList={handleViewCardList}
        />

        {/* 内容区域 */}
        {renderContent()}
      </div>
    </PageLayout>
  )
})
